import styled from "@emotion/styled";
import type { FormikProps } from "formik";
import { useEffect, useMemo } from "react";

import { useAssetUrl } from "@skilitics/assets";
import {
  Button,
  DestructiveButton,
  FieldImageAsset,
  FieldLabel,
  FieldSelect,
  FieldTags,
  FieldText,
  FieldTextSuggestions,
  Form,
  FormRowField,
  FormikForm,
  type FileMediaAssetValue,
  type FormFormatMessages,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { NodeTemplateCategory, NodeTemplateScopeType } from "@skilitics/nautilus-endpoint-definition";

import { ScrollableContentWrapper } from "../browse-templates/shared";
import { Buttons } from "./Buttons";
import { addEmptyOption, categoriesToOptions, scopeOptions } from "./helpers";

export const templateThumbnailSize = { width: 232, height: 126 };

const FormBody = styled.div`
  overflow: hidden;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;

  height: 100%;
  width: 100%;

  ${FormBody} {
    flex: 1 1 100%;
  }
`;

/**
 * Asset which hasn't been uploaded yet.
 * Used for default thumbnails but could be used for selected files.
 * Matches the shape of MediaAssetValue that the FieldImageAsset sets on
 * the field value, since wrapping what that sets is a bit of a pain.
 * The general media assets logic could probably use this shape in some
 * form later?
 */
export type LocalMediaAssetValue = { source: "local"; file: File };

export type ThumbnailAssetValue = FileMediaAssetValue | LocalMediaAssetValue;

export type FormValues = {
  title: string;
  description: string;
  tags: string[];
  scope: NodeTemplateScopeType;
  category: string;
  thumbnail?: ThumbnailAssetValue | undefined;
};

interface Props {
  initialValues: FormValues;
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
  onRemove?: () => void;
  createDefaultThumbnail?: () => Promise<Blob>;
  canSetSystemScope?: boolean;
  canAddCategories?: boolean;
  deleting?: boolean;
  saving?: boolean;
  categories?: NodeTemplateCategory[];
}

export const NodeTemplateForm = ({
  initialValues,
  onSubmit,
  onCancel,
  onRemove,
  createDefaultThumbnail,
  canSetSystemScope,
  canAddCategories,
  deleting,
  saving,
  categories,
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const categoryOptions = useMemo(
    () => addEmptyOption(categoriesToOptions(categories?.filter(({ name }) => name) ?? []), "None"),
    [categories],
  );

  const availableScopeOptions = useMemo(
    () => (canSetSystemScope ? scopeOptions : scopeOptions.filter((o) => o.value !== "system")),
    [canSetSystemScope],
  );

  // if system scope can't take it off unless can set it back
  const canEditScope = initialValues.scope !== "system" || canSetSystemScope;

  const busy = saving || deleting;

  return (
    <FormikForm<FormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      formatStrings={formFormatStrings}
      explicitLabels
      dataTest="edit-template-form"
    >
      {({ dirty, values, setFieldValue: untypedSetFieldValue }: FormikProps<FormValues>) => {
        // Dunno why setFieldValue() isn't typed, setValues() is?
        const setFieldValue = untypedSetFieldValue as <K extends keyof FormValues>(
          field: K,
          value: FormValues[K],
        ) => Promise<void>;
        return (
          <StyledForm $labelAlignment="inline" $labelWidth={80}>
            <ScrollableContentWrapper>
              <FormRowField<FormValues> name="title">
                <FieldLabel />
                <FieldText />
              </FormRowField>

              <FormRowField<FormValues> name="description">
                <FieldLabel />
                <FieldText />
              </FormRowField>

              <FormRowField<FormValues> name="tags">
                <FieldLabel />
                <FieldTags />
              </FormRowField>

              <FormRowField<FormValues> name="category">
                <FieldLabel />
                <FieldTextSuggestions options={categoryOptions} freeSolo={canAddCategories} />
              </FormRowField>

              <FormRowField<FormValues> name="scope">
                <FieldLabel />
                <FieldSelect options={availableScopeOptions} disabled={!canEditScope} />
              </FormRowField>

              <FormRowField<FormValues> name="thumbnail">
                <FieldLabel />
                <ThumbnailRowContainer>
                  {!values.thumbnail && (
                    <>
                      <FieldImageAsset />

                      {createDefaultThumbnail && (
                        <Button
                          variant="outlined"
                          size="small"
                          data-test="create-default-thumbnail-button"
                          onClick={async () => {
                            const data = await createDefaultThumbnail();
                            // File name doesn't really matter, since the user doesn't see it in the current UX,
                            // even if they edit the template we don't fetch the metadata so it just shows "Uploaded Media".
                            // This might be confusing if we just put all assets into an asset library if we add that in
                            // the future, though I expect we wouldn't want these in there anyway?
                            const name = "template-thumbnail.webp";
                            await setFieldValue("thumbnail", {
                              source: "local",
                              file: new File([data], name, { type: data.type }),
                            });
                          }}
                        >
                          Create Default
                        </Button>
                      )}
                    </>
                  )}

                  {values.thumbnail && (
                    <>
                      {values.thumbnail.source === "local" ? (
                        <LocalPreview data={values.thumbnail.file} />
                      ) : (
                        <AssetPreview assetId={values.thumbnail.assetId} />
                      )}

                      <Button
                        variant="outlined"
                        size="small"
                        data-test="clear-thumbnail-button"
                        onClick={async () => {
                          await setFieldValue("thumbnail", undefined);
                        }}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </ThumbnailRowContainer>
              </FormRowField>
            </ScrollableContentWrapper>

            <Buttons>
              <Button disabled={busy} onClick={onCancel} data-test="cancel-button">
                {formatMessage("cancelLabel")}
              </Button>

              {onRemove && (
                <DestructiveButton variant="contained" disabled={busy} onClick={onRemove} data-test="delete-button">
                  {formatMessage(deleting ? "deletingLabel" : "deleteLabel")}
                </DestructiveButton>
              )}

              <Button type="submit" variant="contained" disabled={busy || !dirty} data-test="save-button">
                {formatMessage(saving ? "savingLabel" : "saveLabel")}
              </Button>
            </Buttons>
          </StyledForm>
        );
      }}
    </FormikForm>
  );
};

const AssetPreview = ({ assetId }: { assetId: string }) => {
  const url = useAssetUrl(assetId);
  return url ? <ThumbnailImage $url={url} /> : null;
};

// Doesn't seem to be an existing DOM or designer component to directly display a Blob image?
const LocalPreview = ({ data }: { data: Blob }) => {
  const url = useMemo(() => URL.createObjectURL(data), [data]);
  useEffect(
    () => () => {
      URL.revokeObjectURL(url);
    },
    [url],
  );
  return <ThumbnailImage $url={url} />;
};

// Unfortunately the preview images in the template panel vs browse templates popup aren't consistently styled ATM.
// This matches the existing size in browse templates, since the panel just uses a max-height.
const ThumbnailImage = styled.div<{ $url: string }>`
  margin: auto;
  width: ${templateThumbnailSize.width}px;
  height: ${templateThumbnailSize.height}px;
  background: transparent center/contain no-repeat url(${({ $url }) => $url});
  border: 1px solid ${({ theme }) => theme.outlineSoft};
  border-radius: ${({ theme }) => theme.borderRadius}px;
`;

const ThumbnailRowContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
  justify-content: start;
  margin-bottom: 0;
`;

const formFormatStrings = {
  titleLabel: {
    en: "Title",
  },
  descriptionLabel: {
    en: "Description",
  },
  tagsLabel: {
    en: "Tags",
  },
  scopeLabel: {
    en: "Scope",
  },
  categoryLabel: {
    en: "Category",
  },
  thumbnailLabel: {
    en: "Thumbnail",
  },
} satisfies FormFormatMessages<FormValues>;

const formatStrings = {
  saveLabel: {
    en: "Save",
  },
  savingLabel: {
    en: "Saving...",
  },
  cancelLabel: {
    en: "Cancel",
  },
  deleteLabel: {
    en: "Delete",
  },
  deletingLabel: {
    en: "Deleting...",
  },
} satisfies FormatMessages;
